import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Eye, Lock, Unlock, ArrowLeftRight, QrCode, CheckCircle2, AlertTriangle, Clock, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import loteService, { type Lote, type LoteStatus } from '@/services/lote';
import localService, { type Local } from '@/services/local';

const STATUS_LABELS: Record<LoteStatus, string> = {
  LIVRE: 'Livre',
  BLOQUEADO: 'Bloqueado',
  QUARENTENA: 'Quarentena',
};
const STATUS_TONES: Record<LoteStatus, Tone> = {
  LIVRE: 'success',
  BLOQUEADO: 'danger',
  QUARENTENA: 'warning',
};

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatDate(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

function diasParaVencer(dataValidade?: string | null): number | null {
  if (!dataValidade) return null;
  const hoje = new Date();
  const validade = new Date(dataValidade);
  return Math.ceil((validade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
}

function getValidadeTone(dataValidade?: string | null): Tone {
  const dias = diasParaVencer(dataValidade);
  if (dias === null) return 'neutral';
  if (dias < 0) return 'danger';
  if (dias <= 30) return 'warning';
  return 'success';
}

function getActionErrorMessage(error: unknown, fallback: string) {
  const data = (error as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  return data?.error || data?.message || fallback;
}

export default function LotesPage() {
  const navigate = useNavigate();
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroValidade, setFiltroValidade] = useState('');

  const [dialogBloquear, setDialogBloquear] = useState(false);
  const [dialogTransferir, setDialogTransferir] = useState(false);
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [loteAcao, setLoteAcao] = useState<Lote | null>(null);
  const [motivoBloqueio, setMotivoBloqueio] = useState('');
  const [responsavelBloqueio, setResponsavelBloqueio] = useState('');
  const [destinoTransferencia, setDestinoTransferencia] = useState('');
  const [quantidadeTransferencia, setQuantidadeTransferencia] = useState(0);
  const [responsavelTransferencia, setResponsavelTransferencia] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      setLotes(await loteService.listar({ status: filtroStatus || undefined }));
    } catch (error) {
      console.error('Erro ao carregar lotes', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localService.listar().then(setLocais).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  const itensFiltrados = useMemo(() => {
    let resultado = lotes;
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter((l) => l.numero_lote.toLowerCase().includes(termo));
    }
    if (filtroValidade === 'VENCIDO') {
      resultado = resultado.filter((l) => (diasParaVencer(l.data_validade) ?? 999) < 0);
    } else if (filtroValidade === 'PROXIMO') {
      resultado = resultado.filter((l) => {
        const dias = diasParaVencer(l.data_validade);
        return dias !== null && dias >= 0 && dias <= 30;
      });
    }
    return resultado;
  }, [lotes, busca, filtroValidade]);

  const lotesLivres = useMemo(() => lotes.filter((l) => l.status === 'LIVRE').length, [lotes]);
  const lotesBloqueados = useMemo(() => lotes.filter((l) => l.status === 'BLOQUEADO').length, [lotes]);
  const lotesQuarentena = useMemo(() => lotes.filter((l) => l.status === 'QUARENTENA').length, [lotes]);
  const lotesVencidos = useMemo(() => lotes.filter((l) => (diasParaVencer(l.data_validade) ?? 999) <= 30).length, [lotes]);

  const abrirBloquear = (item: Lote) => {
    setErroAcao(null);
    setLoteAcao(item);
    setMotivoBloqueio('');
    setResponsavelBloqueio('');
    setDialogBloquear(true);
  };

  const confirmarBloqueio = async () => {
    if (!loteAcao || !motivoBloqueio || !responsavelBloqueio) return;
    setProcessando(loteAcao.id);
    try {
      await loteService.bloquear(loteAcao.id, motivoBloqueio, responsavelBloqueio);
      setDialogBloquear(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao bloquear lote', error);
      setErroAcao(getActionErrorMessage(error, 'Não foi possível bloquear o lote.'));
    } finally {
      setProcessando(null);
    }
  };

  const liberar = async (item: Lote) => {
    setErroAcao(null);
    setProcessando(item.id);
    try {
      await loteService.liberar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao liberar lote', error);
      setErroAcao(getActionErrorMessage(error, 'Não foi possível liberar o lote.'));
    } finally {
      setProcessando(null);
    }
  };

  const abrirTransferir = (item: Lote) => {
    setErroAcao(null);
    setLoteAcao(item);
    setDestinoTransferencia('');
    setQuantidadeTransferencia(Number(item.quantidade));
    setResponsavelTransferencia('');
    setDialogTransferir(true);
  };

  const confirmarTransferencia = async () => {
    if (!loteAcao || !destinoTransferencia || !quantidadeTransferencia) return;
    setProcessando(loteAcao.id);
    try {
      await loteService.transferir(loteAcao.id, {
        destino: destinoTransferencia,
        quantidade: quantidadeTransferencia,
        responsavel: responsavelTransferencia || undefined,
      });
      setDialogTransferir(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao transferir lote', error);
      setErroAcao(getActionErrorMessage(error, 'Não foi possível transferir o lote.'));
    } finally {
      setProcessando(null);
    }
  };

  const abrirExcluir = (item: Lote) => {
    setErroAcao(null);
    setLoteAcao(item);
    setDialogExcluir(true);
  };

  const confirmarExclusao = async () => {
    if (!loteAcao) return;
    setProcessando(loteAcao.id);
    try {
      await loteService.excluir(loteAcao.id);
      setDialogExcluir(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir lote', error);
      setErroAcao(getActionErrorMessage(error, 'Não foi possível excluir o lote.'));
    } finally {
      setProcessando(null);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<QrCode size={21} strokeWidth={2} />}
        title="Controle de Lotes"
        description="Rastreabilidade, validade e movimentação de lotes de produtos."
        actions={
          <Button type="button" onClick={() => navigate('/producao/estoque/lotes/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Lote
          </Button>
        }
      />

      {erroAcao ? (
        <div className="nx-form-alert nx-form-alert--error" role="alert">
          {erroAcao}
        </div>
      ) : null}

      <div className="nx-kpi-grid">
        <div className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Livres</span>
            <span className="nx-kpi-icon"><CheckCircle2 size={16} strokeWidth={2} /></span>
          </div>
          <div className="nx-kpi-body">
            <span className="nx-kpi-value">{lotesLivres}</span>
          </div>
        </div>
        <div className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Bloqueados</span>
            <span className="nx-kpi-icon"><Lock size={16} strokeWidth={2} /></span>
          </div>
          <div className="nx-kpi-body">
            <span className="nx-kpi-value">{lotesBloqueados}</span>
          </div>
        </div>
        <div className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Quarentena</span>
            <span className="nx-kpi-icon"><Clock size={16} strokeWidth={2} /></span>
          </div>
          <div className="nx-kpi-body">
            <span className="nx-kpi-value">{lotesQuarentena}</span>
          </div>
        </div>
        <div className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Vencidos/Próximos</span>
            <span className="nx-kpi-icon"><AlertTriangle size={16} strokeWidth={2} /></span>
          </div>
          <div className="nx-kpi-body">
            <span className="nx-kpi-value">{lotesVencidos}</span>
          </div>
        </div>
      </div>

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar lote..."
                />
              </div>
              <Select filter value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Todos os status</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              <Select filter value={filtroValidade} onChange={(e) => setFiltroValidade(e.target.value)}>
                <option value="">Validade</option>
                <option value="VENCIDO">Vencido</option>
                <option value="PROXIMO">Próximo ao vencimento</option>
              </Select>
            </div>
          }
        />

        {!loading && itensFiltrados.length === 0 ? (
          <EmptyState message="Nenhum lote encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Produto</th>
                  <th>Status</th>
                  <th>Quantidade</th>
                  <th>Validade</th>
                  <th>Localização</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  itensFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.numero_lote}</td>
                      <td>
                        <div>{item.produto_nome}</div>
                        <span className="nx-chip">{item.produto_codigo}</span>
                      </td>
                      <td>
                        <StatusBadge label={STATUS_LABELS[item.status]} tone={STATUS_TONES[item.status]} />
                      </td>
                      <td>{formatNumber(item.quantidade)}</td>
                      <td>
                        {item.data_validade ? (
                          <StatusBadge label={formatDate(item.data_validade)} tone={getValidadeTone(item.data_validade)} />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{item.localizacao || '-'}</td>
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Visualizar" onClick={() => navigate(`/producao/estoque/lotes/${item.id}/visualizar`)}>
                            <Eye size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton label="Editar" onClick={() => navigate(`/producao/estoque/lotes/${item.id}/editar`)}>
                            <Pencil size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Bloquear"
                            variant="danger"
                            onClick={() => abrirBloquear(item)}
                            disabled={item.status === 'BLOQUEADO' || processando === item.id}
                          >
                            <Lock size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Liberar"
                            onClick={() => liberar(item)}
                            disabled={item.status === 'LIVRE' || processando === item.id}
                          >
                            <Unlock size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Transferir"
                            onClick={() => abrirTransferir(item)}
                            disabled={processando === item.id}
                          >
                            <ArrowLeftRight size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label="Excluir"
                            variant="danger"
                            onClick={() => abrirExcluir(item)}
                            disabled={processando === item.id}
                          >
                            <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogBloquear} onOpenChange={(next) => !processando && setDialogBloquear(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquear Lote {loteAcao?.numero_lote ?? ''}</DialogTitle>
            <DialogDescription>Informe o motivo e o responsável pelo bloqueio.</DialogDescription>
          </DialogHeader>
          <div className="nx-form-grid">
            <Field label="Motivo *" htmlFor="bloqueio-motivo" className="nx-sngpc-field--full">
              <Textarea
                id="bloqueio-motivo"
                rows={3}
                value={motivoBloqueio}
                onChange={(e) => setMotivoBloqueio(e.target.value)}
              />
            </Field>
            <Field label="Responsável *" htmlFor="bloqueio-responsavel" className="nx-sngpc-field--full">
              <Input
                id="bloqueio-responsavel"
                value={responsavelBloqueio}
                onChange={(e) => setResponsavelBloqueio(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={!!processando}>Cancelar</Button>
            </DialogClose>
            <Button
              type="button"
              variant="danger"
              loading={processando === loteAcao?.id}
              loadingText="Bloqueando..."
              disabled={!motivoBloqueio || !responsavelBloqueio}
              onClick={confirmarBloqueio}
            >
              Confirmar Bloqueio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogExcluir} onOpenChange={(next) => !processando && setDialogExcluir(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir Lote {loteAcao?.numero_lote ?? ''}</DialogTitle>
            <DialogDescription>
              Tem certeza de que deseja excluir este lote? Esta ação não poderá ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={!!processando}>Cancelar</Button>
            </DialogClose>
            <Button
              type="button"
              variant="danger"
              loading={processando === loteAcao?.id}
              loadingText="Excluindo..."
              onClick={confirmarExclusao}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogTransferir} onOpenChange={(next) => !processando && setDialogTransferir(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transferir Lote {loteAcao?.numero_lote ?? ''}</DialogTitle>
            <DialogDescription>Informe o destino e a quantidade a transferir.</DialogDescription>
          </DialogHeader>
          <div className="nx-form-grid">
            <Field label="Destino *" htmlFor="transferencia-destino" className="nx-sngpc-field--full">
              <Select
                id="transferencia-destino"
                value={destinoTransferencia}
                onChange={(e) => setDestinoTransferencia(e.target.value)}
              >
                <option value="">Selecione</option>
                {locais.map((l) => (
                  <option key={l.id} value={l.descricao}>{l.codigo} - {l.descricao}</option>
                ))}
              </Select>
            </Field>
            <Field label="Quantidade *" htmlFor="transferencia-quantidade">
              <DecimalInput
                id="transferencia-quantidade"
                value={quantidadeTransferencia}
                onValueChange={(value) => setQuantidadeTransferencia(value ?? 0)}
              />
            </Field>
            <Field label="Responsável" htmlFor="transferencia-responsavel">
              <Input
                id="transferencia-responsavel"
                value={responsavelTransferencia}
                onChange={(e) => setResponsavelTransferencia(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={!!processando}>Cancelar</Button>
            </DialogClose>
            <Button
              type="button"
              loading={processando === loteAcao?.id}
              loadingText="Transferindo..."
              disabled={!destinoTransferencia || !quantidadeTransferencia}
              onClick={confirmarTransferencia}
            >
              Confirmar Transferência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
